import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthResponse } from '@clinikchat/shared';
import { epochKeyRing } from '@clinikchat/shared';
import { useCryptoStore } from './cryptoStore';
import { usePresenceStore } from './presenceStore';
import { queryClient } from '../lib/queryClient';

interface AuthState {
  accessToken: string | null;
  user: AuthResponse['user'] | null;
  /** Persisted flag — true when a session exists (survives reload without PII). */
  hasSession: boolean;
  setAuth: (data: AuthResponse) => void;
  setUser: (user: AuthResponse['user'] | null) => void;
  setAccessToken: (token: string) => void;
  logout: () => void;
}

/**
 * The only Cache Storage bucket that can hold PHI-adjacent API responses
 * (see vite.config.ts runtimeCaching). The workbox precache and the
 * static-assets bucket hold only the app shell; wiping those on logout
 * forced a full re-download of the shell and broke the "update available"
 * prompt on the login page for installed PWAs.
 */
export const PHI_CACHE_NAME = 'api-cache';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      hasSession: false,
      setAuth: (data) =>
        set({
          accessToken: data.accessToken,
          user: data.user,
          hasSession: true,
        }),
      // Clearing the in-memory user (e.g. a consent gate that withholds the
      // profile) must not end the session — only logout() does that.
      setUser: (user) => set((state) => ({ user, hasSession: user ? true : state.hasSession })),
      setAccessToken: (token) => set({ accessToken: token }),
      logout: () => {
        useCryptoStore.getState().clearKeys();
        // The epoch key ring is a module singleton that outlives the auth
        // session — without this, a different user logging in in the same
        // tab inherits the previous user's channel keys.
        epochKeyRing.clearAll();
        // Every sign-out path (manual, forced on refresh failure, unlock
        // dialog, password change, account deletion) funnels through here.
        // Drop in-flight requests first so they cannot repopulate the cache,
        // then empty it: otherwise the next user in this tab can see the
        // previous user's channels, messages and profile.
        void queryClient.cancelQueries();
        queryClient.clear();
        usePresenceStore.setState({ statuses: {} });
        // Purge the service worker cache that may contain health data (APP 11).
        if (typeof window !== 'undefined' && 'caches' in window) {
          void caches.delete(PHI_CACHE_NAME).catch(() => undefined);
        }
        set({ accessToken: null, user: null, hasSession: false });
      },
    }),
    {
      name: 'clinikchat-auth',
      // Do NOT persist the refresh token to localStorage — it is delivered
      // via an httpOnly cookie and must stay out of JS-readable storage so
      // an XSS cannot exfiltrate it.
      // accessToken intentionally NOT persisted; useSilentRefresh restores it on mount.
      // Persist only a session-exists flag — never store user PII (email,
      // displayName) in localStorage. The full user object is populated
      // from /api/v1/users/me after silent refresh succeeds (APP 11).
      //
      // Persist the flag itself, not `!!state.user`: between the refresh
      // (setAccessToken, user still null) and /users/me resolving — or for
      // as long as a consent/MFA gate withholds the profile — a reload would
      // otherwise read hasSession:false and land on /login with a live cookie.
      partialize: (state) => ({
        hasSession: state.hasSession,
      }),
    },
  ),
);
